import { ITransactionDto, ITransactionStatisticDto } from './dtos'
import { ITransaction, ITransactionStatistic } from './models'
import dayjs from 'dayjs'

export const mapTransactionFromDto = (dto: ITransactionDto): ITransaction => {
	const {
		id,
		transactionType,
		effectiveDate,
		acquisitionDate,
		needsReview,
		inAmount,
		inCurrency,
		inPriceInBaseCurrency,
		inValuationTotal,
		inTransactionsSource,
		inWallet,
		outAmount,
		outCurrency,
		outPriceInBaseCurrency,
		outValuationTotal,
		outTransactionsSource,
		outWallet,
		feeOriginalAmount,
		feeOriginalCurrency,
		feePriceInBaseCurrency,
		feeValuation,
		forkBaseCurrency,
		note,
		group,
		blockChainTransactionId,
		integrationId,
		properties,
		additionalCosts,
		isMarginLossInValue,
		isMarginProfitInValue,
		marginLossValue,
		marginProfitValue,
		wallet,
		capitalLoss,
		flowType,
		// strings
		inAmountString,
		inPriceInBaseCurrencyString,
		inValuationTotalString,
		outAmountString,
		outPriceInBaseCurrencyString,
		outValuationTotalString,
		feeOriginalAmountString,
		feePriceInBaseCurrencyString,
		feeValuationString,
	} = dto

	return {
		_dto: dto,
		id,
		type: transactionType,
		date: dayjs(effectiveDate, 'YYYY-MM-DDTHH:mm:ss').toDate(),
		dateString: dayjs(effectiveDate, 'YYYY-MM-DDTHH:mm:ss').format('YYYY-MM-DD'),
		acquisitionDate: acquisitionDate ? dayjs(acquisitionDate, 'YYYY-MM-DDTHH:mm:ss').toDate() : undefined,
		in: {
			amount: inAmount,
			amountString: inAmountString,
			perCoin: inPriceInBaseCurrency,
			perCoinString: inPriceInBaseCurrencyString,
			currency: inCurrency,
			valuation: inValuationTotal,
			valuationString: inValuationTotalString,
			source: inTransactionsSource,
			wallet: inWallet?.name || inWallet?.id ? inWallet : undefined,
		},
		out: {
			amount: outAmount,
			amountString: outAmountString,
			perCoin: outPriceInBaseCurrency,
			perCoinString: outPriceInBaseCurrencyString,
			currency: outCurrency,
			source: outTransactionsSource,
			valuation: outValuationTotal,
			valuationString: outValuationTotalString,
			wallet: outWallet?.name || outWallet?.id ? outWallet : undefined,
		},
		fee: {
			amount: feeOriginalAmount,
			amountString: feeOriginalAmountString,
			currency: feeOriginalCurrency,
			perCoin: feePriceInBaseCurrency,
			perCoinString: feePriceInBaseCurrencyString,
			valuation: feeValuation,
			valuationString: feeValuationString,
		},
		additionalCosts,
		needsReview: Boolean(needsReview && needsReview.needsReviewEnums.length > 0),
		approved: !!needsReview?.needsReviewApproved,
		blockchainId: blockChainTransactionId,
		group,
		note,
		warning: needsReview?.needsReviewEnums || [],
		forkBaseCurrency,
		integrationId,
		properties,
		isMarginLossInValue: isMarginLossInValue === true,
		isMarginProfitInValue: isMarginProfitInValue === true,
		marginLossValue,
		marginProfitValue,
		capitalLoss: capitalLoss || undefined,
		wallet,
		flowType,
	}
}

export const mapTransactionToDto = ({
	_dto,
	type,
	date,
	acquisitionDate,
	in: _in,
	out,
	fee,
	approved,
	blockchainId,
	group,
	note,
	forkBaseCurrency,
	properties,
	additionalCosts,
	marginLossValue,
	marginProfitValue,
	isMarginLossInValue,
	isMarginProfitInValue,
	wallet,
	capitalLoss,
	flowType,
}: ITransaction): Omit<ITransactionDto, 'id' | 'userId'> => ({
	..._dto,
	blockChainTransactionId: blockchainId,
	effectiveDate: dayjs(date).format('YYYY-MM-DDTHH:mm:ss'),
	acquisitionDate: acquisitionDate ? dayjs(acquisitionDate).format('YYYY-MM-DDTHH:mm:ss') : undefined,
	additionalCosts,
	feeOriginalAmount: fee?.amount,
	feeOriginalCurrency: fee?.currency,
	feePriceInBaseCurrency: fee?.perCoin,
	feeValuation: fee?.valuation,
	forkBaseCurrency,
	group,
	inAmount: _in?.amount,
	inCurrency: _in?.currency,
	inPriceInBaseCurrency: _in?.perCoin,
	inTransactionsSource: _in?.source,
	inValuationTotal: _in?.valuation,
	inWallet: _in?.wallet,
	flowType,
	needsReview: _dto?.id
		? {
				needsReviewEnums: [],
				..._dto?.needsReview,
				needsReviewApproved: !!approved,
		  }
		: undefined,
	note,
	outAmount: out?.amount,
	outCurrency: out?.currency,
	outPriceInBaseCurrency: out?.perCoin,
	outTransactionsSource: out?.source,
	outValuationTotal: out?.valuation,
	outWallet: out?.wallet,
	transactionType: type,
	properties,
	marginLossValue,
	marginProfitValue,
	isMarginLossInValue,
	isMarginProfitInValue,
	wallet,
	capitalLoss,
})

export const mapTransactionStatisticFromDto = ({
	transactions,
	allTransactions,
	needsReview,
	dateOfFirstTransaction,
	dateOfLastTransaction,
}: ITransactionStatisticDto): ITransactionStatistic => ({
	transactions,
	all: allTransactions,
	needsReview,
	earliestTransactionDate: dateOfFirstTransaction ? dayjs(dateOfFirstTransaction).toDate() : undefined,
	latestTransactionDate: dateOfLastTransaction ? dayjs(dateOfLastTransaction).toDate() : undefined,
})
